import React from 'react';
import { Grid } from '@material-ui/core';
import { Headline } from '@cambridgeassessment/cambridge-ui';
import { Role, Roles } from '@cambridgeassessment/lib-ee-commons';
import { useSelector } from 'react-redux';
import { StyledContainer } from './Landing.styles';
import GridItem from '../../components/GridItem/GridItem';
import { RootState } from '../../store';
import { brandedFC } from '../../branding/brandedFC';
import { Authorized } from 'lib-browser';

const AdminLanding: React.FC = () => {
  const selectedRole: Role | null = useSelector((state: RootState) => state.userData.selectedRole);

  const isAssManager = selectedRole?.name === Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER;
  const text = isAssManager ? 'View' : 'Define and set';

  const BrandedConsortiaManagementLink: React.FC = brandedFC(({ showConsortiaManagementLink }) => {
    return showConsortiaManagementLink ? (
      <GridItem title={'Manage consortia'} path={'/manageConsortia'} qaId={'priming-category-consortia-management'}>
        Create, edit and delete consortia.
      </GridItem>
    ) : null;
  });

  const BrandedRMSubmissionLink: React.FC = brandedFC(({ showRmFileSubmissionLink }) => {
    return showRmFileSubmissionLink ? (
      <GridItem title={'RM file submission failures'} path={'/resendFilesToRm'} qaId={'priming-category-rm-submission'}>
        Search for and resend RM file submission failures.
      </GridItem>
    ) : null;
  });

  const BrandedMalpracticeReportsLink: React.FC = brandedFC(({ showMalpracticeReportsLink }) => {
    return showMalpracticeReportsLink ? (
      <GridItem
        title={'Malpractice reports'}
        path={'/malpracticeReports'}
        qaId={'priming-category-malpractice-reports'}
      >
        View and analyse Similarity Index Reports for submitted candidate work.
      </GridItem>
    ) : null;
  });

  return (
    <StyledContainer>
      <Headline>{selectedRole?.description}</Headline>
      <Grid container data-qa-id='list-priming-categories' xs={6} item={true}>
        <Authorized
          selectedRole={selectedRole}
          roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR, Roles.SUBMIT_COMPLIANCE]}
          readOnlyRoles={[Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER]}
        >
          <GridItem title={'Series business rules'} path={'/series'} qaId={'priming-category-series'}>
            {text} requirements for current and future series.
          </GridItem>
        </Authorized>
        <Authorized
          selectedRole={selectedRole}
          roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR, Roles.SUBMIT_COMPLIANCE]}
          readOnlyRoles={[Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER]}
        >
          <GridItem title={'Component business rules'} path={'/components'} qaId={'priming-category-components'}>
            {text} requirements for components for current and future series.
          </GridItem>
        </Authorized>
        <Authorized
          selectedRole={selectedRole}
          roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_COMPLIANCE, Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER]}
        >
          <BrandedMalpracticeReportsLink />
        </Authorized>
        <Authorized
          selectedRole={selectedRole}
          roles={[
            Roles.SUBMIT_SUPER_ADMIN,
            Roles.SUBMIT_ADMINISTRATOR,
            Roles.SUBMIT_COMPLIANCE,
            Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER,
          ]}
        >
          <GridItem
            title={'Submission reports'}
            path={'/submissionReports'}
            qaId={'priming-category-submission-reports'}
          >
            View and analyse submissions for the current series.
          </GridItem>
        </Authorized>
        <Authorized
          selectedRole={selectedRole}
          roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR, Roles.SUBMIT_COMPLIANCE]}
        >
          <GridItem title={'Replacement files'} path={'/replacementFiles'} qaId={'priming-category-replacement-files'}>
            Allow centres or candidates to replace work for the current series.
          </GridItem>
        </Authorized>
        <Authorized
          selectedRole={selectedRole}
          roles={[
            Roles.SUBMIT_SUPER_ADMIN,
            Roles.SUBMIT_ADMINISTRATOR,
            Roles.SUBMIT_COMPLIANCE,
            Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER,
          ]}
        >
          <GridItem title={'Training reports'} path={'/trainingReports'} qaId={'priming-category-training-reports'}>
            View and analyse reports on the candidate work marked as ok to use in training materials.
          </GridItem>
        </Authorized>
        <Authorized selectedRole={selectedRole} roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR]}>
          <BrandedConsortiaManagementLink />
        </Authorized>
        <Authorized
          selectedRole={selectedRole}
          roles={[
            Roles.SUBMIT_SUPER_ADMIN,
            Roles.SUBMIT_ADMINISTRATOR,
            Roles.SUBMIT_COMPLIANCE,
            Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER,
          ]}
        >
          <GridItem
            title={'View and download candidate work'}
            path={'/downloadCandidateWork'}
            qaId={'priming-category-work-downloading'}
          >
            Search for, view and download submitted candidate work.
          </GridItem>
        </Authorized>
        <Authorized selectedRole={selectedRole} roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR]}>
          <GridItem
            title={'Manage moderated sample selections'}
            path={'/manageSampleChanges'}
            qaId={'priming-category-manage-sample-changes'}
          >
            View and manage moderated sample selection.
          </GridItem>
        </Authorized>
        <Authorized selectedRole={selectedRole} roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR]}>
          <BrandedRMSubmissionLink />
        </Authorized>
      </Grid>
    </StyledContainer>
  );
};

export default AdminLanding;
